import mime from 'mime';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import type { UserJobApplicationCv } from '@xing-com/crate-common-graphql-types';
import { getIconByFileType } from '@xing-com/crate-jobs-domain-apply-helpers';
import type {
  UploadedFile,
  UseUploadResult,
} from '@xing-com/crate-jobs-domain-apply-uploads';
import { List } from '@xing-com/list';

import { FileListItem, NONE_ITEM_ID } from '../file-list-item';

type TrackNwtEventProps = {
  eventSchema: 'basic';
  event: string;
  context: string;
  element: string;
  page: string;
};

type Props = {
  uploads: UploadedFile[];
  onDeleteUpload?: UseUploadResult['handleDeleteUpload'];
  userRecentCvs: UserJobApplicationCv[];
  isRequiredCv: boolean;
  updateFormUploadId: (cvUploadId: string) => void;
  trackNwtEvent: (eventProps: TrackNwtEventProps) => void;
};

export const UploadedCvList: React.FC<Props> = ({
  uploads,
  userRecentCvs,
  onDeleteUpload,
  updateFormUploadId,
  isRequiredCv,
  trackNwtEvent,
}) => {
  const { formatMessage } = useIntl();
  const [selectedCvId, setSelectedCvId] = useState(userRecentCvs[0].key);
  const [lastSelectedCvId, setLastSelectedCvId] = useState(
    userRecentCvs[0].key
  );
  const cvUploadId = uploads[0]?.id?.toString();

  const onRadioButtonClick = useCallback(
    (cvFileItemKey: string): void => {
      setSelectedCvId(cvFileItemKey);
      trackNwtEvent({
        eventSchema: 'basic',
        event: 'clicked',
        context: 'instant_apply',
        element: 'saved_cv_toggle',
        page: 'jobs/apply',
      });
      if (cvFileItemKey !== cvUploadId) {
        setLastSelectedCvId(cvFileItemKey);
      }
    },
    [cvUploadId, trackNwtEvent]
  );

  useEffect(() => {
    if (cvUploadId) {
      setSelectedCvId(cvUploadId);
    }
  }, [cvUploadId]);

  useEffect(() => {
    if (!cvUploadId && selectedCvId !== lastSelectedCvId) {
      setSelectedCvId(lastSelectedCvId);
    }
  }, [cvUploadId, selectedCvId, lastSelectedCvId]);

  useEffect(() => {
    if (selectedCvId) {
      updateFormUploadId(selectedCvId !== NONE_ITEM_ID ? selectedCvId : '');
    }
  }, [selectedCvId, updateFormUploadId]);

  return (
    <List>
      {uploads.length > 0 &&
        uploads.map((upload) => {
          const { id, fileName, fileType, fileSize, progress } = upload;

          return (
            <FileListItem
              key={id}
              itemId={id ? id.toString() : ''}
              name={fileName}
              Icon={getIconByFileType(fileType)}
              size={fileSize}
              progress={progress}
              onDelete={() => onDeleteUpload?.(upload)}
              onRadioButtonClick={onRadioButtonClick}
              isSelected={selectedCvId === id?.toString()}
              trackNwtEvent={trackNwtEvent}
            />
          );
        })}
      {userRecentCvs.map((recentCV) => {
        const { key, name, url, lastUsedAt } = recentCV;
        const fileType = mime.getType(key);

        return (
          <FileListItem
            key={key}
            itemId={key}
            name={name}
            Icon={getIconByFileType(fileType)}
            onRadioButtonClick={onRadioButtonClick}
            url={url}
            lastUsedAt={lastUsedAt}
            isSelected={selectedCvId === key}
            trackNwtEvent={trackNwtEvent}
          />
        );
      })}
      {!isRequiredCv ? (
        <FileListItem
          itemId={NONE_ITEM_ID}
          onRadioButtonClick={onRadioButtonClick}
          isSelected={selectedCvId === NONE_ITEM_ID}
          name={formatMessage({ id: 'JOBS_INSTANT_APPLY_DO_NOT_USE_CV_LABEL' })}
          trackNwtEvent={trackNwtEvent}
        />
      ) : null}
    </List>
  );
};
